import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import EventCard from './EventCard';
import OrganizationEventDetail from './OrganizationEventDetail';
import type { Event } from '../types/event';
import styles from './OrganizationDashboard.enhanced.module.css';
import { eventService, organizationService } from '../services/api';
import { ROUTES } from '../constants/routes';
import { 
  IconPlus, 
  IconFilter, 
  IconSearch, 
  IconChevronDown, 
  IconChevronUp,
  IconCalendarEvent,
  IconChartBar,
  IconChartPie,
  IconGraph,
  IconLoader
} from '@tabler/icons-react';
import { 
  PieChart, Pie, Cell, 
  ResponsiveContainer, 
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend 
} from 'recharts';
import { getSavedCount, getInterestCount, getViewCount } from '../utils/eventCounters';

export default function OrganizationDashboard() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [userCreatedEvents, setUserCreatedEvents] = useState<Event[]>([]);
  const navigate = useNavigate();
  
  // Filtres et tri
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'past'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'popularity'>('newest');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  
  // Affichage des statistiques
  const [expandedStats, setExpandedStats] = useState<boolean>(true);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 768);
  
  // Fonction pour détecter la taille d'écran mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Fonction pour générer des données pour le graphique mensuel basées sur les événements CRÉÉS par l'utilisateur
  const generateMonthlyData = () => {
    // Si aucun événement créé par l'utilisateur, retourner un tableau vide
    if (!userCreatedEvents || userCreatedEvents.length === 0) {
      console.log("Aucun événement créé par l'utilisateur disponible pour générer les données mensuelles");
      return [];
    }

    console.log(`Génération des statistiques pour ${userCreatedEvents.length} événements créés par l'utilisateur`);

    // Trouver d'abord les mois uniques de tous les événements de l'utilisateur
    const eventMonths = new Map<string, { 
      date: Date, 
      nom: string, 
      Intérêts: number, 
      Sauvegardes: number, 
      Vues: number 
    }>();

    // Pour chaque événement créé par l'utilisateur
    userCreatedEvents.forEach((event, index) => {
      // Convertir la date de l'événement en objet Date
      const eventDate = new Date(event.dateTime);
      
      // Créer une clé unique pour ce mois/année
      const monthKey = `${eventDate.getFullYear()}-${eventDate.getMonth() + 1}`;
      
      // Utiliser nos fonctions standardisées pour obtenir les compteurs
      const interests = getInterestCount(event);
      const savedBy = getSavedCount(event);
      const views = getViewCount(event);
      
      // Pour le diagnostic des 3 premiers événements
      if (index < 3) {
        console.log(`Événement #${event.id} "${event.title}" (${eventDate.toLocaleDateString()})`);
        console.log(`  └─ Compteurs standardisés: interests=${interests}, savedBy=${savedBy}, views=${views}`);
        console.log(`  └─ Mois/Année: ${monthKey}`);
      }
      
      // Si ce mois n'existe pas encore dans la map, l'ajouter
      if (!eventMonths.has(monthKey)) {
        const monthDate = new Date(eventDate.getFullYear(), eventDate.getMonth(), 1);
        eventMonths.set(monthKey, {
          date: monthDate,
          nom: monthDate.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
          Intérêts: interests,
          Sauvegardes: savedBy,
          Vues: views
        });
        
        if (index < 3) {
          console.log(`  └─ Nouveau mois ajouté: ${monthDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`);
        }
      } else {
        // Sinon, ajouter les compteurs au mois existant
        const existingMonth = eventMonths.get(monthKey)!;
        existingMonth.Intérêts += interests;
        existingMonth.Sauvegardes += savedBy;
        existingMonth.Vues += views;
        
        if (index < 3) {
          console.log(`  └─ Ajouté au mois existant: ${existingMonth.nom}`);
          console.log(`  └─ Nouvelles valeurs: Intérêts=${existingMonth.Intérêts}, Sauvegardes=${existingMonth.Sauvegardes}, Vues=${existingMonth.Vues}`);
        }
      }
    });

    // Convertir la Map en tableau et trier par date
    const monthArray = Array.from(eventMonths.values())
      .sort((a, b) => a.date.getTime() - b.date.getTime());
    
    // Log des résultats finaux pour le débogage
    console.log(`Données mensuelles finales pour le graphique (${monthArray.length} mois):`);
    monthArray.forEach(month => {
      console.log(`  ${month.nom}: Intérêts=${month.Intérêts}, Sauvegardes=${month.Sauvegardes}, Vues=${month.Vues}`);
    });
    
    return monthArray;
  };
  
  const generateEventStatusData = () => {
    // Compter les événements actifs et passés
    const activeCount = events.filter(event => new Date(event.dateTime) >= new Date()).length;
    const pastCount = events.filter(event => new Date(event.dateTime) < new Date()).length;
    // Pour les brouillons, nous n'avons pas cette information, donc on met 0
    const draftCount = 0;
    
    return [
      { name: 'Actifs', value: activeCount, color: '#4f46e5' },
      { name: 'Terminés', value: pastCount, color: '#94a3b8' },
      { name: 'Brouillons', value: draftCount, color: '#e2e8f0' }
    ];
  };
  
  const monthlyData = useMemo(() => generateMonthlyData(), [userCreatedEvents]);
  const eventStatusData = useMemo(() => generateEventStatusData(), [events]);
  
  // Récupération des événements
  useEffect(() => {
    fetchEvents();
    // Endpoint /me maintenant disponible, on peut réactiver l'appel
    fetchOrganizations();
  }, []);
  
  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log("Récupération des événements pour l'organisation...");
      
      // Récupérer d'abord les événements créés par l'utilisateur
      let userCreatedEvents = [];
      
      try {
        // Utiliser getUserEvents pour récupérer les événements créés par l'utilisateur
        userCreatedEvents = await eventService.getUserEvents();
        console.log(`📊 Dashboard Organisation: ${userCreatedEvents.length} événements créés par l'utilisateur récupérés`);
        console.log(`ℹ️ Dashboard Organisation: Affichage UNIQUEMENT des événements créés par l'utilisateur connecté`);
        
        // Vérifier la structure des compteurs pour le premier événement (si disponible)
        if (userCreatedEvents.length > 0) {
          console.log("Structure détaillée du premier événement créé par l'utilisateur:", {
            id: userCreatedEvents[0].id,
            title: userCreatedEvents[0].title,
            _count: userCreatedEvents[0]._count,
            interestedCount: userCreatedEvents[0].interestedCount,
            savedByCount: userCreatedEvents[0].savedByCount,
            savedCount: userCreatedEvents[0].savedCount
          });
        }
      } catch (error) {
        console.warn("Erreur lors de la récupération des événements créés par l'utilisateur:", error);
        userCreatedEvents = [];
      }
      
      // Ne stocker que les événements créés par l'utilisateur pour l'affichage
      setUserCreatedEvents(userCreatedEvents);
      setEvents(userCreatedEvents);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Erreur lors du chargement des événements. Veuillez réessayer.');
      setLoading(false);
    }
  };
  
  const fetchOrganizations = async () => {
    try {
      // Récupérer les organisations de l'utilisateur connecté via le nouvel endpoint /me
      const response = await organizationService.getUserOrganizations();
      setOrganizations(response);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      // En cas d'échec, utiliser une organisation de démonstration comme fallback
      setOrganizations([
        { id: 1, name: "Organisation de démonstration", isActive: true }
      ]);
    }
  };
  
  const handleViewOrganization = (orgId: number) => {
    navigate(`/organization/${orgId}`);
  };
  
  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
  };
  
  // Gestion des intérêts
  const handleInterestClick = (eventId: number) => {
    console.log('Interest clicked for event:', eventId);
    // Implémentation réelle à ajouter ici
  };
  
  // Gestion des sauvegardes
  const handleSaveClick = (eventId: number) => {
    console.log('Save clicked for event:', eventId);
    // Implémentation réelle à ajouter ici
  };
  
  // Calcul des événements filtrés et triés
  const sortedAndFilteredEvents = events
    .filter(event => {
      // Filtrer par recherche
      const matchesSearch = 
        event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (event.description && event.description.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Filtrer par onglet sélectionné
      const now = new Date();
      const eventDate = new Date(event.dateTime);
      const isActive = eventDate >= now;
      
      if (activeTab === 'active') return matchesSearch && isActive;
      if (activeTab === 'past') return matchesSearch && !isActive;
      
      return matchesSearch; // 'all'
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime();
      } else if (sortBy === 'oldest') {
        return new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime();
      } else { // popularity
        const aPopularity = (a._count?.interests || 0) + (a._count?.savedBy || 0);
        const bPopularity = (b._count?.interests || 0) + (b._count?.savedBy || 0);
        return bPopularity - aPopularity;
      }
    });
  
  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.dashboardHeader}>
        <h1 className={styles.dashboardTitle}>Tableau de bord des organisations</h1>
        <p className={styles.dashboardSubtitle}>
          Gérez et suivez vos organisations et leurs événements
        </p>
      </div>

      {/* Section des organisations */}
      <div className={styles.organizationsSection}>
        <div className={styles.sectionHeader}>
          <h2>Vos organisations</h2>
          <button className={styles.createButton} onClick={() => navigate('/org/new')}>
            <IconPlus size={16} />
            Créer une organisation
          </button>
        </div>

        <div className={styles.organizationsList}>
          {organizations.map(org => (
            <div 
              key={org.id} 
              className={styles.organizationCard}
              onClick={() => handleViewOrganization(org.id)}
            >
              <div className={styles.orgLogoContainer}>
                {org.logoUrl ? (
                  <img src={org.logoUrl} alt={org.name} className={styles.orgLogo} />
                ) : (
                  <div className={styles.orgLogoPlaceholder}>{org.name.charAt(0)}</div>
                )}
              </div>
              <div className={styles.orgInfo}>
                <h3 className={styles.orgName}>{org.name}</h3>
                <p className={styles.orgStats}>
                  <IconCalendarEvent size={16} />
                  {org._count?.events || 0} événements
                </p>
              </div>
              <button className={styles.viewOrgButton}>
                Voir le détail
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Section des statistiques globales */}
      <div className={styles.statsSection}>
        <div className={styles.sectionHeader}>
          <h2>
            <IconChartBar size={20} />
            Statistiques
          </h2>
          <button 
            className={styles.toggleStatsButton}
            onClick={() => setExpandedStats(!expandedStats)}
          >
            {expandedStats ? (
              <>
                <IconChevronUp size={16} />
                Masquer
              </>
            ) : (
              <>
                <IconChevronDown size={16} />
                Afficher
              </>
            )}
          </button>
        </div>
        
        {expandedStats && (
          <div className={styles.chartsContainer}>
            {events.length > 0 ? (
              <>
                {/* Graphique de tendance mensuelle - réduit la hauteur sur mobile */}
                <div className={styles.chartCard}>
                  <h3 className={styles.chartTitle}>
                    <IconGraph size={18} />
                    Tendances mensuelles
                  </h3>
                  <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
                    <LineChart
                      data={monthlyData}
                      margin={{ 
                        top: 10, 
                        right: isMobile ? 10 : 30, 
                        left: isMobile ? 0 : 10, 
                        bottom: isMobile ? 10 : 5 
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={!isMobile} />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: isMobile ? 10 : 12 }} 
                        tickMargin={isMobile ? 5 : 10}
                      />
                      <YAxis 
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        width={isMobile ? 25 : 35}
                        tickFormatter={(value) => isMobile ? value : `${value}`}
                      />
                      <Tooltip 
                        contentStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                        itemStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                      />
                      <Legend 
                        verticalAlign={isMobile ? "top" : "bottom"}
                        height={isMobile ? 30 : 36}
                        iconSize={isMobile ? 8 : 10}
                        wrapperStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="Intérêts" 
                        stroke="#4f46e5" 
                        strokeWidth={isMobile ? 1.5 : 2}
                        activeDot={{ r: isMobile ? 4 : 6 }}
                        dot={{ r: isMobile ? 3 : 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="Sauvegardes" 
                        stroke="#f59e0b" 
                        strokeWidth={isMobile ? 1.5 : 2}
                        activeDot={{ r: isMobile ? 4 : 6 }}
                        dot={{ r: isMobile ? 3 : 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="Vues" 
                        stroke="#10b981" 
                        strokeWidth={isMobile ? 1.5 : 2}
                        activeDot={{ r: isMobile ? 4 : 6 }}
                        dot={{ r: isMobile ? 3 : 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                
                {/* Graphique de statut des événements - réduit la taille sur mobile */}
                <div className={styles.chartCard}>
                  <h3 className={styles.chartTitle}>
                    <IconChartPie size={18} />
                    Statut des événements
                  </h3>
                  <ResponsiveContainer width="100%" height={isMobile ? 180 : 250}>
                    <PieChart>
                      <Pie
                        data={eventStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={!isMobile}
                        outerRadius={isMobile ? 60 : 80}
                        fill="#8884d8"
                        dataKey="value"
                        label={isMobile ? undefined : ({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {eventStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value, name) => [`${value} événements`, name]}
                        contentStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                        itemStyle={{ fontSize: isMobile ? '10px' : '12px' }}
                      />
                      <Legend 
                        verticalAlign={isMobile ? "bottom" : "bottom"}
                        height={isMobile ? 30 : 36}
                        iconSize={isMobile ? 8 : 10}
                        wrapperStyle={{ fontSize: isMobile ? '10px' : '12px', paddingTop: isMobile ? '5px' : '10px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <div className={styles.emptyStateMessage}>
                <p>Aucune donnée statistique disponible - créez des événements pour voir les statistiques</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section des événements */}
      <div className={styles.eventsSection}>
        <div className={styles.sectionHeader}>
          <h2>
            <IconCalendarEvent size={20} />
            Vos événements
          </h2>
          <button className={styles.createButton} onClick={() => navigate(ROUTES.EVENT_NEW)}>
            <IconPlus size={16} />
            Créer un événement
          </button>
        </div>
        
        <div className={styles.filterBar}>
          <div className={styles.tabsContainer}>
            <button 
              className={`${styles.tab} ${activeTab === 'all' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('all')}
            >
              Tous
            </button>
            <button 
              className={`${styles.tab} ${activeTab === 'active' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('active')}
            >
              Actifs
            </button>
            <button 
              className={`${styles.tab} ${activeTab === 'past' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('past')}
            >
              Terminés
            </button>
          </div>
          
          <div className={styles.filterControls}>
            <div className={styles.searchContainer}>
              <IconSearch size={18} className={styles.searchIcon} />
              <input 
                type="text" 
                placeholder="Rechercher..." 
                className={styles.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <button 
              className={styles.filterButton}
              onClick={() => setShowFilters(!showFilters)}
            >
              <IconFilter size={18} />
              Filtres
            </button>
            
            <select 
              className={styles.sortSelect}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
            >
              <option value="newest">Plus récents</option>
              <option value="oldest">Plus anciens</option>
              <option value="popularity">Popularité</option>
            </select>
          </div>
        </div>
        
        {showFilters && (
          <div className={styles.advancedFilters}>
            {/* Filtres avancés à implémenter */}
            <p>Options de filtrage supplémentaires à venir...</p>
          </div>
        )}
        
        {loading ? (
          <div className={styles.loadingContainer}>
            <IconLoader size={30} className={styles.spinnerIcon} />
            <p>Chargement des événements...</p>
          </div>
        ) : error ? (
          <div className={styles.errorContainer}>
            <p>{error}</p>
            <button onClick={fetchEvents} className={styles.retryButton}>Réessayer</button>
          </div>
        ) : (
          <div className={styles.eventsContainer}>
            {sortedAndFilteredEvents.length > 0 ? (
              <>
                <div className={styles.eventsList}>
                  {sortedAndFilteredEvents.map(event => (
                    <div key={event.id} className={styles.eventCardWrapper}>
                      <EventCard 
                        event={event}
                        onClick={() => handleEventClick(event)}
                        onInterestClick={() => handleInterestClick(event.id)}
                        onSaveClick={() => handleSaveClick(event.id)}
                        isOrganizationDashboard={true}
                      />
                    </div>
                  ))}
                </div>
                
                {selectedEvent && (
                  <div className={styles.eventDetailContainer}>
                    <OrganizationEventDetail 
                      event={selectedEvent}
                      onClose={() => setSelectedEvent(null)}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className={styles.emptyStateMessage}>
                <div>
                  <p>Vous n'avez pas encore d'événements</p>
                  <button className={styles.createButton} onClick={() => navigate(ROUTES.EVENT_NEW)} style={{ marginTop: '1rem' }}>
                    <IconPlus size={16} />
                    Créer votre premier événement
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
